import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Space, Tag, Popconfirm, Image, Spin, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { portfolioAPI } from '../services/api';

const { Title } = Typography;

export default function Portfolio() {
    const [portfolios, setPortfolios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(null);

    useEffect(() => {
        fetchPortfolios();
    }, []);

    const fetchPortfolios = async () => {
        try {
            setLoading(true);
            const response = await portfolioAPI.getAll();
            setPortfolios(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching portfolios:', error);
            message.error('Failed to fetch portfolios');
            setPortfolios([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setDeleteLoading(id);
            await portfolioAPI.delete(id);
            setPortfolios(portfolios.filter((p) => (p.id || p._id) !== id));
            message.success('Portfolio deleted successfully');
        } catch (error) {
            console.error('Error deleting portfolio:', error);
            message.error('Failed to delete portfolio');
        } finally {
            setDeleteLoading(null);
        }
    };

    const categoryLabels = {
        design: 'Design & Planning',
        structure: 'Structure Specialist',
        'slf-pbg': 'SLF & PBG Review',
    };

    const categoryColors = {
        design: 'blue',
        structure: 'green',
        'slf-pbg': 'orange',
    };

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const baseURL = API_URL.replace('/api', '');

    const columns = [
        {
            title: 'Image',
            dataIndex: 'thumbnail',
            key: 'thumbnail',
            width: 100,
            render: (thumbnail) => {
                if (!thumbnail) return '-';
                const imageUrl = thumbnail.startsWith('http') ? thumbnail : `${baseURL}${thumbnail}`;
                return <Image src={imageUrl} width={60} height={60} style={{ objectFit: 'cover', borderRadius: '4px' }} />;
            },
        },
        {
            title: 'Project Name',
            dataIndex: 'title',
            key: 'title',
            width: '30%',
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => (
                <Tag color={categoryColors[category] || 'default'}>
                    {categoryLabels[category] || category}
                </Tag>
            ),
        },
        {
            title: 'Layanan',
            dataIndex: 'service',
            key: 'service',
            render: (service) => (
                service ? <Tag color="purple">{service.title}</Tag> : <Tag color="default">-</Tag>
            ),
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Link to={`/portfolio/edit/${record.id || record._id}`}>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            size="small"
                        >
                            Edit
                        </Button>
                    </Link>
                    <Popconfirm
                        title="Are you sure you want to delete this portfolio?"
                        onConfirm={() => handleDelete(record.id || record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={deleteLoading === (record.id || record._id)}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading portfolios..." />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>Portfolio Management</Title>
                <Link to="/portfolio/new">
                    <Button type="primary" icon={<PlusOutlined />} size="large">
                        Add New Portfolio
                    </Button>
                </Link>
            </div>

            <Table
                columns={columns}
                dataSource={portfolios}
                rowKey={(record) => record.id || record._id}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} portfolios`,
                }}
                locale={{
                    emptyText: 'No portfolios found',
                }}
            />
        </div>
    );
}
